import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import {
  ArrowLeft, Volume2, VolumeX, Play, Pause, Check, X, Star, 
  Trophy, Home, BookOpen, ChevronRight, Loader2, Heart
} from 'lucide-react';
import VoiceSelector, { VOICE_OPTIONS } from '@/components/VoiceSelector';

// Build version: 2026-02-25-v2 - Dynamic story loading from API

// Emoji mapping for stories
const STORY_EMOJIS = {
  'creation': '🌍', 'adam-eve': '🍎', 'noah': '🚢', 'rainbow': '🌈', 'babel': '🗼',
  'abraham': '⭐', 'isaac': '👶', 'jacob': '🪜', 'joseph': '🧥', 'moses': '🔥',
  'red-sea': '🌊', 'commandments': '📜', 'joshua': '🏰', 'samson': '💪', 'ruth': '🌾',
  'david': '👑', 'goliath': '⚔️', 'solomon': '💡', 'elijah': '🦅', 'daniel': '🦁',
  'jonah': '🐋', 'esther': '👸', 'jesus': '✝️', 'christmas': '🎄', 'wise-men': '⭐',
  'miracle': '✨', 'parable': '📖', 'lazarus': '🪦', 'cross': '✝️', 'resurrection': '🌅',
  'saul': '👑', 'deborah': '⚖️', 'gideon': '🔦'
};

const getStoryEmoji = (storyId) => {
  for (const [key, emoji] of Object.entries(STORY_EMOJIS)) {
    if (storyId?.toLowerCase().includes(key)) return emoji;
  }
  return '📖';
};

const BibleStoryActivity = () => {
  const { storyId } = useParams();
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('child');
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [phase, setPhase] = useState('intro');
  const [story, setStory] = useState(null);
  const [storyParts, setStoryParts] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentPart, setCurrentPart] = useState(0);
  const [isReading, setIsReading] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startTime] = useState(Date.now());
  const [extendedStory, setExtendedStory] = useState(null);
  const [loadingExtended, setLoadingExtended] = useState(false);
  const [showExtended, setShowExtended] = useState(false);
  // TTS Settings - user controllable
  const [ttsVoice, setTtsVoice] = useState('shimmer'); // shimmer (expressive) or nova (warm)
  const [ttsSpeed, setTtsSpeed] = useState(0.75); // Slower default for story reading
  
  const speechRef = useRef(null);

  useEffect(() => {
    fetchStoryData();
    return () => {
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch {}
      }
    };
  }, [storyId, childId]);

  const fetchStoryData = async () => {
    setLoading(true);
    try {
      // Fetch child data
      if (childId) {
        const childRes = await api.get(`/children/${childId}`);
        setChild(childRes.data);
      }

      // Fetch story from API
      const storyRes = await api.get(`/bible/story/${storyId}`);
      const storyData = storyRes.data.story || storyRes.data;
      
      if (!storyData || !storyData.title) {
        throw new Error('Story not found');
      }
      
      setStory({
        id: storyData.id,
        title: storyData.title,
        image: getStoryEmoji(storyData.id),
        summary: storyData.summary,
        reference: storyData.reference,
        lesson: storyData.lesson
      });

      // Generate story parts from summary for reading
      const summaryParts = storyData.summary ? 
        storyData.summary.split('. ').filter(s => s.trim()).map((text, idx) => ({
          part: idx + 1,
          text: text.endsWith('.') ? text : text + '.'
        })) : [];
      
      // Add the lesson as a concluding part
      if (storyData.lesson) {
        summaryParts.push({
          part: summaryParts.length + 1,
          text: `Lesson: ${storyData.lesson}`
        });
      }
      
      setStoryParts(summaryParts.length > 0 ? summaryParts : [{ part: 1, text: storyData.summary || 'This is a wonderful Bible story!' }]);

      // Generate simple questions based on the story
      const generatedQuestions = [
        {
          question: `What is this story about?`,
          options: [storyData.title, 'A different story', 'Something else', 'I don\'t know'],
          answer: storyData.title,
          hint: 'Look at the title!'
        },
        {
          question: `What is the lesson from this story?`,
          options: [
            storyData.lesson?.split(' ').slice(0, 5).join(' ') + '...' || 'Trust God',
            'Be mean to others',
            'Don\'t listen',
            'Give up'
          ],
          answer: storyData.lesson?.split(' ').slice(0, 5).join(' ') + '...' || 'Trust God',
          hint: 'Think about what we learned!'
        }
      ];
      
      setQuestions(generatedQuestions);
      
      // Fetch extended narrative in background
      fetchExtendedStory();
      
    } catch (error) {
      console.error('Failed to fetch story:', error);
      toast.error('Failed to load story');
      // Fallback story
      setStory({
        id: storyId,
        title: storyId?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Bible Story',
        image: getStoryEmoji(storyId),
        summary: 'This is a wonderful story from the Bible that teaches us about God\'s love.',
        lesson: 'God loves us and wants us to follow Him.'
      });
      setStoryParts([{ part: 1, text: 'This is a wonderful story from the Bible that teaches us about God\'s love.' }]);
      setQuestions([
        { question: 'Who loves us?', options: ['God', 'Nobody', 'Only friends', 'Animals'], answer: 'God', hint: 'He created us!' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchExtendedStory = async () => {
    setLoadingExtended(true);
    try {
      const response = await api.get(`/bible/story/${storyId}/extended?age_group=children`);
      if (response.data.extended_narrative) {
        setExtendedStory(response.data);
        // Parse extended narrative into parts for better reading experience
        const narrative = response.data.extended_narrative;
        const paragraphs = narrative.split('\n\n').filter(p => p.trim());
        if (paragraphs.length > 1) {
          const extendedParts = paragraphs.map((text, idx) => ({
            part: idx + 1,
            text: text.trim()
          }));
          // Don't override immediately - let user choose
          setExtendedStory({
            ...response.data,
            parts: extendedParts
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch extended story:', error);
    } finally {
      setLoadingExtended(false);
    }
  };

  const switchToExtendedStory = () => {
    if (extendedStory?.parts) {
      setStoryParts(extendedStory.parts);
      setCurrentPart(0);
      setShowExtended(true);
      toast.success('Switched to full story mode!');
    }
  };

  const audioRef = useRef(null);

  const speakText = async (text, onEnd = null) => {
    try {
      setIsReading(true);
      // Use OpenAI TTS for natural reading with user-configurable voice and speed
      const response = await api.post(`/voice/speak?text=${encodeURIComponent(text)}&voice=${ttsVoice}&speed=${ttsSpeed}`, {}, {
        responseType: 'blob'
      });
      
      const audioUrl = URL.createObjectURL(response.data);
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.onended = () => {
          setIsReading(false);
          if (onEnd) onEnd();
        };
        audioRef.current.onerror = () => {
          setIsReading(false);
          // Fallback to browser TTS
          fallbackSpeak(text, onEnd);
        };
        await audioRef.current.play();
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsReading(false);
      toast.error('Could not generate audio. Please try again.');
      if (onEnd) onEnd();
    }
  };

  const stopReading = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsReading(false);
  };

  const handleAnswer = (answer) => {
    if (showFeedback) return;
    setSelectedAnswer(answer);
    setShowFeedback(true);
    
    const isCorrect = answer === questions[currentQuestion]?.answer;
    if (isCorrect) {
      setScore(prev => prev + 1);
      speakText('Great job! That\'s correct!');
    } else {
      speakText('Good try! Let\'s learn from this.');
    }
  };

  const nextQuestion = () => {
    setSelectedAnswer(null);
    setShowFeedback(false);
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setPhase('results');
      if (score >= questions.length * 0.7) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      }
      // Record activity
      recordActivity();
    }
  };

  const recordActivity = async () => {
    if (!childId) return;
    try {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      await api.post('/activities/record', {
        child_id: childId,
        activity_type: 'bible_story',
        activity_id: storyId,
        score: Math.round((score / questions.length) * 100),
        time_spent_seconds: timeSpent,
        completed: true
      });
      
      // Auto-complete the daily task if this was started from a daily task
      const activeTaskId = localStorage.getItem('activeTaskId');
      const activeTaskModule = localStorage.getItem('activeTaskModule');
      
      if (activeTaskId && activeTaskModule === 'bible' && score >= questions.length * 0.7) {
        try {
          await api.post(`/daily-tasks/${activeTaskId}/complete`);
          toast.success('Daily task completed! +XP earned!', {
            icon: '🎯',
            duration: 3000
          });
          localStorage.removeItem('activeTaskId');
          localStorage.removeItem('activeTaskModule');
        } catch (taskError) {
          console.log('Task may already be completed:', taskError);
        }
      }
    } catch (error) {
      console.error('Failed to record activity:', error);
    }
  };

  const nextPart = () => {
    stopReading();
    if (currentPart < storyParts.length - 1) {
      setCurrentPart(prev => prev + 1);
    } else {
      setPhase('assessment');
      setCurrentQuestion(0);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-100 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-purple-600 font-medium">Loading story...</p>
        </div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-100 to-pink-100 flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-lg mb-4">Story not found</p>
          <Link to={`/bible-stories/${childId}`}>
            <Button>Back to Stories</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-100 to-pink-100">
      {/* Hidden audio element for TTS */}
      <audio ref={audioRef} />
      
      {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md p-4 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            <Link to={`/bible-stories/${childId}`}>
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <span className="text-2xl">{story.image}</span>
              <span className="font-bold text-purple-800 truncate max-w-[200px]">{story.title}</span>
            </div>
            <div className="flex items-center gap-2">
              {child && (
                <span className="text-sm text-purple-600">{child.name}</span>
              )}
            </div>
          </div>
          
          {/* Voice Settings Bar */}
          <div className="mt-3">
            <VoiceSelector
              voice={ttsVoice}
              onVoiceChange={setTtsVoice}
              speed={ttsSpeed}
              onSpeedChange={setTtsSpeed}
              theme="light"
              className="bg-purple-50 border-purple-200"
            />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4">
        <AnimatePresence mode="wait">
          {/* Intro Phase */}
          {phase === 'intro' && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center py-8"
            >
              <div className="text-8xl mb-6">{story.image}</div>
              <h1 className="font-fredoka text-3xl text-purple-800 mb-4">{story.title}</h1>
              {story.reference && (
                <p className="text-purple-600 mb-2">{story.reference}</p>
              )}
              <p className="text-lg text-slate-600 mb-8">{story.summary}</p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  onClick={() => setPhase('story')}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-6 text-lg rounded-full"
                  data-testid="start-story-btn"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Quick Read
                </Button>
                
                {/* Full Story Button */}
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => {
                    if (extendedStory?.parts) {
                      switchToExtendedStory();
                      setPhase('story');
                    } else {
                      toast.info('Loading full story...');
                      fetchExtendedStory();
                      setTimeout(() => {
                        if (extendedStory?.parts) {
                          switchToExtendedStory();
                        }
                        setPhase('story');
                      }, 2000);
                    }
                  }}
                  className="border-2 border-purple-400 text-purple-600 hover:bg-purple-50 px-8 py-6 text-lg rounded-full"
                  disabled={loadingExtended}
                  data-testid="full-story-btn"
                >
                  {loadingExtended ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <BookOpen className="w-5 h-5 mr-2" />
                      Full Story
                    </>
                  )}
                </Button>
              </div>
              
              {extendedStory && (
                <p className="text-sm text-purple-500 mt-4">
                  ✨ Extended story with dialogue and activities available!
                </p>
              )}
            </motion.div>
          )}

          {/* Story Phase */}
          {phase === 'story' && (
            <motion.div
              key="story"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Progress 
                value={(currentPart + 1) / storyParts.length * 100} 
                className="h-2 mb-6"
              />
              
              <Card className="border-0 shadow-xl mb-6">
                <CardContent className="p-6">
                  {showExtended && (
                    <div className="flex justify-center mb-4">
                      <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        ✨ Full Story Mode
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-4">
                    <span className="text-6xl">{story.image}</span>
                  </div>
                  
                  <motion.p
                    key={currentPart}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-xl text-slate-700 leading-relaxed text-center mb-6 whitespace-pre-line"
                  >
                    {storyParts[currentPart]?.text}
                  </motion.p>

                  <div className="flex justify-center gap-4">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => isReading ? stopReading() : speakText(storyParts[currentPart]?.text)}
                      className="rounded-full"
                    >
                      {isReading ? <VolumeX className="w-5 h-5 mr-2" /> : <Volume2 className="w-5 h-5 mr-2" />}
                      {isReading ? 'Stop' : 'Read Aloud'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPart(prev => Math.max(0, prev - 1))}
                  disabled={currentPart === 0}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={nextPart}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {currentPart < storyParts.length - 1 ? 'Next' : 'Take Quiz'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Assessment Phase */}
          {phase === 'assessment' && questions.length > 0 && (
            <motion.div
              key="assessment"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="text-center mb-6">
                <span className="text-4xl mb-2 block">🧠</span>
                <h2 className="font-fredoka text-xl text-purple-800">Quiz Time!</h2>
                <p className="text-slate-500">Question {currentQuestion + 1} of {questions.length}</p>
              </div>

              <Progress value={(currentQuestion + 1) / questions.length * 100} className="h-2 mb-6" />

              <Card className="border-0 shadow-xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-center mb-6">
                    {questions[currentQuestion]?.question}
                  </h3>

                  <div className="grid grid-cols-1 gap-3">
                    {questions[currentQuestion]?.options.map((option, idx) => {
                      const isSelected = selectedAnswer === option;
                      const isCorrect = option === questions[currentQuestion]?.answer;
                      const showResult = showFeedback;

                      let buttonStyle = 'bg-white hover:bg-purple-50 border-2 border-purple-200';
                      if (showResult && isCorrect) {
                        buttonStyle = 'bg-green-100 border-2 border-green-500';
                      } else if (showResult && isSelected && !isCorrect) {
                        buttonStyle = 'bg-red-100 border-2 border-red-500';
                      } else if (isSelected) {
                        buttonStyle = 'bg-purple-100 border-2 border-purple-500';
                      }

                      return (
                        <Button
                          key={idx}
                          variant="outline"
                          className={`h-auto py-4 px-6 text-left justify-start ${buttonStyle}`}
                          onClick={() => handleAnswer(option)}
                          disabled={showFeedback}
                          data-testid={`answer-option-${idx}`}
                        >
                          <span className="text-lg">{option}</span>
                          {showResult && isCorrect && <Check className="w-5 h-5 ml-auto text-green-600" />}
                          {showResult && isSelected && !isCorrect && <X className="w-5 h-5 ml-auto text-red-600" />}
                        </Button>
                      );
                    })}
                  </div>

                  {showFeedback && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 text-center"
                    >
                      <Button onClick={nextQuestion} className="bg-purple-600 hover:bg-purple-700">
                        {currentQuestion < questions.length - 1 ? 'Next Question' : 'See Results'}
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Results Phase */}
          {phase === 'results' && (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="text-6xl mb-4">
                {score >= questions.length * 0.7 ? '🌟' : '💪'}
              </div>
              <h2 className="font-fredoka text-3xl text-purple-800 mb-2">
                {score >= questions.length * 0.7 ? 'Amazing Job!' : 'Good Effort!'}
              </h2>
              <p className="text-xl text-slate-600 mb-6">
                You got {score} out of {questions.length} correct!
              </p>

              <Card className="bg-gradient-to-r from-purple-100 to-pink-100 border-0 mb-8">
                <CardContent className="p-6">
                  <h3 className="font-bold text-purple-800 mb-2">Remember:</h3>
                  <p className="text-purple-700">{story.lesson}</p>
                </CardContent>
              </Card>

              {/* Extended Story Extras - Discussion Questions & Activities */}
              {extendedStory && showExtended && (
                <>
                  {/* Memory Verse */}
                  {extendedStory.memory_verse && (
                    <Card className="bg-gradient-to-r from-amber-100 to-yellow-100 border-0 mb-4">
                      <CardContent className="p-6">
                        <h3 className="font-bold text-amber-800 mb-2">📖 Memory Verse:</h3>
                        <p className="text-amber-700 italic">{extendedStory.memory_verse}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Discussion Questions */}
                  {extendedStory.discussion_questions?.length > 0 && (
                    <Card className="bg-gradient-to-r from-blue-100 to-cyan-100 border-0 mb-4">
                      <CardContent className="p-6">
                        <h3 className="font-bold text-blue-800 mb-3">💬 Talk About It:</h3>
                        <ul className="space-y-2">
                          {extendedStory.discussion_questions.map((q, idx) => (
                            <li key={idx} className="text-blue-700 flex items-start gap-2">
                              <span className="text-blue-500 font-bold">{idx + 1}.</span>
                              {q}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {/* Activities */}
                  {extendedStory.activities?.length > 0 && (
                    <Card className="bg-gradient-to-r from-green-100 to-emerald-100 border-0 mb-8">
                      <CardContent className="p-6">
                        <h3 className="font-bold text-green-800 mb-3">🎨 Fun Activities:</h3>
                        <ul className="space-y-2">
                          {extendedStory.activities.map((activity, idx) => (
                            <li key={idx} className="text-green-700 flex items-start gap-2">
                              <span className="text-green-500">✨</span>
                              {activity}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => {
                    setPhase('intro');
                    setCurrentPart(0);
                    setCurrentQuestion(0);
                    setScore(0);
                    setShowExtended(false);
                  }}
                  variant="outline"
                  className="w-full"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Read Again
                </Button>
                <Link to={`/bible-stories/${childId}`} className="w-full">
                  <Button className="w-full bg-purple-600 hover:bg-purple-700">
                    <Heart className="w-4 h-4 mr-2" />
                    More Stories
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default BibleStoryActivity;
